import {
  ChangeDetectionStrategy, Component, TemplateRef, ViewChild,
  input, model, OnDestroy, effect, inject
} from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';

@Component({
  selector: 'app-dialog',
  template: `
    <ng-template #dialogTemplate>
      <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-in fade-in">
        <div
          class="w-full max-w-lg rounded-lg bg-white shadow-xl animate-in zoom-in-95 fade-in duration-200"
          role="dialog"
          [attr.aria-modal]="true"
          [attr.aria-labelledby]="titleId"
        >
          @if (title()) {
            <div class="flex items-center justify-between p-6 pb-4 border-b border-border">
              <h2 [id]="titleId" class="text-lg font-semibold">{{ title() }}</h2>
              <button
                (click)="close()"
                class="rounded-md p-1.5 hover:bg-muted/10 transition-colors text-muted"
                aria-label="Close dialog"
              >&times;</button>
            </div>
          }
          <div class="p-6">
            <ng-content />
          </div>
          @if (hasFooter) {
            <div class="flex items-center justify-end gap-2 px-6 pb-6">
              <ng-content select="[dialogFooter]" />
            </div>
          }
        </div>
      </div>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogComponent implements OnDestroy {
  title = input('');
  isOpen = model(false);
  hasFooter = true;

  protected readonly titleId = `dialog-title-${crypto.randomUUID()}`;

  @ViewChild('dialogTemplate') private readonly dialogTemplate!: TemplateRef<unknown>;

  private readonly cdkDialog = inject(Dialog);
  private dialogRef: DialogRef<unknown> | null = null;

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        this.openDialog();
      } else {
        this.closeDialog();
      }
    });
  }

  private openDialog(): void {
    if (this.dialogRef) return;
    this.dialogRef = this.cdkDialog.open(this.dialogTemplate, {
      panelClass: ['cdk-dialog-container-transparent'],
      hasBackdrop: false,
      disableClose: false,
    });
    this.dialogRef.closed.subscribe(() => {
      this.isOpen.set(false);
      this.dialogRef = null;
    });
  }

  private closeDialog(): void {
    this.dialogRef?.close();
    this.dialogRef = null;
  }

  close(): void {
    this.isOpen.set(false);
  }

  ngOnDestroy(): void {
    this.closeDialog();
  }
}
